import { useGetList, type Identifier, type RaRecord } from "ra-core";
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const SEARCH_LIMIT = 20;

export interface RecordPickerProps<RecordType extends RaRecord> {
  resource: string;
  /** Text of the button opening the picker. */
  label: string;
  icon?: ReactNode;
  searchPlaceholder: string;
  emptyText: string;
  /** Records already linked, left out of the choices. */
  excludedIds: Identifier[];
  filter?: Record<string, unknown>;
  sort?: { field: string; order: "ASC" | "DESC" };
  renderOption: (record: RecordType) => ReactNode;
  onSelect: (record: RecordType) => void;
  disabled?: boolean;
}

/**
 * Button opening a searchable list of records to link (staff to an
 * opportunity, an opportunity or an event to a partner...).
 */
export const RecordPicker = <RecordType extends RaRecord>({
  resource,
  label,
  icon,
  searchPlaceholder,
  emptyText,
  excludedIds,
  filter,
  sort = { field: "name", order: "ASC" },
  renderOption,
  onSelect,
  disabled,
}: RecordPickerProps<RecordType>) => {
  const [isOpen, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { data = [] } = useGetList<RecordType>(
    resource,
    {
      pagination: { page: 1, perPage: SEARCH_LIMIT },
      sort,
      filter: search ? { ...filter, q: search } : (filter ?? {}),
    },
    { enabled: isOpen },
  );
  const options = data.filter((record) => !excludedIds.includes(record.id));

  const handleSelect = (record: RecordType) => {
    onSelect(record);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={disabled}>
          {icon}
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-80" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder={searchPlaceholder}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((record) => (
                <CommandItem
                  key={record.id}
                  value={String(record.id)}
                  onSelect={() => handleSelect(record)}
                >
                  {renderOption(record)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
